/**
 * Keyboard, swipe, and on-screen pad. Turns raw input into a screen-space
 * direction, which the model then converts to a maze direction with
 * `GameModel.toMazeDir`.
 */

import { GameModel } from './game.js';

/** Screen-space direction constants. `game.ts` maps these to maze space. */
export const NORTH = 0;
export const EAST = 1;
export const SOUTH = 2;
export const WEST = 3;

/**
 * Bind keyboard input. Returns a function that unbinds. The listener sits on
 * `window` because arrows should work whether the canvas has focus or not —
 * `<canvas>` is not focusable by default, and a player who has to tab to it
 * first has already lost the race.
 */
export function bindKeyboard(model: GameModel): () => void {
  const onKey = (event: KeyboardEvent): void => {
    const dir = keyToDir(event.key);
    if (dir === null) return;
    // Preventing default keeps arrow keys from scrolling the page underneath
    // the game — which on desktop is the whole visible area.
    event.preventDefault();
    model.tryMove(dir);
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}

function keyToDir(key: string): number | null {
  switch (key) {
    case 'ArrowUp':
    case 'w':
    case 'W':
    case 'k':
      return NORTH;
    case 'ArrowRight':
    case 'd':
    case 'D':
    case 'l':
      return EAST;
    case 'ArrowDown':
    case 's':
    case 'S':
    case 'j':
      return SOUTH;
    case 'ArrowLeft':
    case 'a':
    case 'A':
    case 'h':
      return WEST;
    default:
      return null;
  }
}

/**
 * Bind swipe input on an element. Returns a function that unbinds.
 *
 * A swipe here is measured in *screen* pixels, and the same threshold — one
 * eighth of the shorter dimension — works whether the phone is held upright
 * or sideways. The model turns the screen direction into a maze direction.
 */
export function bindSwipe(target: HTMLElement, model: GameModel): () => void {
  let start: { x: number; y: number } | null = null;

  const onDown = (event: PointerEvent): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Only the primary pointer participates — a stray second finger on a
    // phone must not cancel a swipe already begun.
    target.setPointerCapture(event.pointerId);
    start = { x: event.clientX, y: event.clientY };
  };

  const onUp = (event: PointerEvent): void => {
    if (start === null) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    start = null;
    // Threshold as a fraction of the target's shorter side, so a small
    // canvas is not less responsive than a large one.
    const rect = target.getBoundingClientRect();
    const threshold = Math.max(20, Math.min(rect.width, rect.height) / 8);
    const abs = Math.max(Math.abs(dx), Math.abs(dy));
    if (abs < threshold) return;
    let dir: number;
    if (Math.abs(dx) > Math.abs(dy)) {
      dir = dx > 0 ? EAST : WEST;
    } else {
      dir = dy > 0 ? SOUTH : NORTH;
    }
    model.tryMove(dir);
  };

  const onCancel = (): void => {
    start = null;
  };

  target.addEventListener('pointerdown', onDown);
  target.addEventListener('pointerup', onUp);
  target.addEventListener('pointercancel', onCancel);

  return () => {
    target.removeEventListener('pointerdown', onDown);
    target.removeEventListener('pointerup', onUp);
    target.removeEventListener('pointercancel', onCancel);
  };
}
